import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortIcon, ariaSortValue } from "@/components/sort-icon";
import { Check, Loader2 } from "lucide-react";
import type { SnipeItAsset } from "@/lib/snipeit/types";

type SortKey = "asset_tag" | "model" | "serial" | "category";
type SortDir = "asc" | "desc";

interface AssetTableProps {
  assets: SnipeItAsset[];
  isLoading: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
  onToggleSort: (key: SortKey) => void;
  selectedIds: Set<number>;
  movingIds: Set<number>;
  movedIds: Set<number>;
  errorIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
}

export function AssetTable({
  assets,
  isLoading,
  sortKey,
  sortDir,
  onToggleSort,
  selectedIds,
  movingIds,
  movedIds,
  errorIds,
  onToggleSelect,
  onToggleSelectAll,
}: AssetTableProps) {
  const allChecked =
    assets.filter((a) => !movedIds.has(a.id)).length > 0 &&
    assets.filter((a) => !movedIds.has(a.id)).every((a) => selectedIds.has(a.id));

  return (
    <div className="border-border bg-card min-h-0 flex-1 overflow-hidden rounded-xl border shadow-sm">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-10">
              <input
                type="checkbox"
                className="accent-primary h-5 w-5 cursor-pointer"
                checked={allChecked}
                onChange={onToggleSelectAll}
              />
            </TableHead>
            <TableHead aria-sort={ariaSortValue("asset_tag", sortKey, sortDir)}>
              <button
                type="button"
                className="hover:text-foreground inline-flex w-full cursor-pointer items-center select-none"
                onClick={() => onToggleSort("asset_tag")}
              >
                Asset Tag
                <SortIcon col="asset_tag" sortKey={sortKey} sortDir={sortDir} />
              </button>
            </TableHead>
            <TableHead aria-sort={ariaSortValue("model", sortKey, sortDir)}>
              <button
                type="button"
                className="hover:text-foreground inline-flex w-full cursor-pointer items-center select-none"
                onClick={() => onToggleSort("model")}
              >
                Modell
                <SortIcon col="model" sortKey={sortKey} sortDir={sortDir} />
              </button>
            </TableHead>
            <TableHead aria-sort={ariaSortValue("serial", sortKey, sortDir)}>
              <button
                type="button"
                className="hover:text-foreground inline-flex w-full cursor-pointer items-center select-none"
                onClick={() => onToggleSort("serial")}
              >
                Seriennummer
                <SortIcon col="serial" sortKey={sortKey} sortDir={sortDir} />
              </button>
            </TableHead>
            <TableHead aria-sort={ariaSortValue("category", sortKey, sortDir)}>
              <button
                type="button"
                className="hover:text-foreground inline-flex w-full cursor-pointer items-center select-none"
                onClick={() => onToggleSort("category")}
              >
                Kategorie
                <SortIcon col="category" sortKey={sortKey} sortDir={sortDir} />
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                Laden…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && assets.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                Keine Geräte an diesem Standort gefunden.
              </TableCell>
            </TableRow>
          )}
          {assets.map((asset) => {
            const isMoved = movedIds.has(asset.id);
            const isMovingThis = movingIds.has(asset.id);
            const hasError = errorIds.has(asset.id);

            return (
              <TableRow
                key={asset.id}
                tabIndex={isMoved || isMovingThis ? undefined : 0}
                role={isMoved || isMovingThis ? undefined : "button"}
                className={`cursor-pointer select-none ${isMoved ? "opacity-40" : hasError ? "bg-destructive/5" : ""}`}
                onClick={() => {
                  if (!isMoved && !isMovingThis) onToggleSelect(asset.id);
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isMoved && !isMovingThis) {
                    e.preventDefault();
                    onToggleSelect(asset.id);
                  }
                }}
              >
                <TableCell className="py-4">
                  {isMovingThis ? (
                    <Loader2 className="text-primary mx-auto h-5 w-5 animate-spin" />
                  ) : isMoved ? (
                    <Check className="mx-auto h-5 w-5 text-green-600" />
                  ) : hasError ? (
                    <span className="text-destructive text-xs">!</span>
                  ) : (
                    <input
                      type="checkbox"
                      className="accent-primary h-5 w-5 cursor-pointer"
                      checked={selectedIds.has(asset.id)}
                      onChange={() => onToggleSelect(asset.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </TableCell>
                <TableCell className="text-foreground py-4 font-mono text-sm">{asset.asset_tag}</TableCell>
                <TableCell className="text-muted-foreground py-4">{asset.model?.name ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground py-4 font-mono text-xs">{asset.serial || "—"}</TableCell>
                <TableCell className="text-muted-foreground py-4">{asset.category?.name ?? "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
